import React, {useState, useEffect} from 'react';
import {Input, InputNumber, Select, Table, Form, Tooltip} from 'antd';
import './customTable.scss';
import http from '../../services/httpService';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useForm} from 'antd/lib/form/Form';
import Toast from '../toast/Toast';
import {t} from 'react-switch-lang';
import {ReactComponent as PlusImg} from './img/plus.svg';
import {ReactComponent as EditImg} from './img/edit.svg';
import {ReactComponent as DeleteImg} from './img/delete.svg';
import {ReactComponent as TickImg} from './img/tick.svg';
import {ReactComponent as CancelImg} from './img/cancel.svg';
import {Spinner} from 'react-bootstrap';
import {getAllRevisions} from '../../services/revisions';
import FindingsTable from '../../app/revisions/components/table/FindingsTable';
import SubTableRisks from './SubTableRisks';

/**
 *
 * @param {object} props
 * @param {string} props.api 'Endpoint for fetching table data'
 * @param {array} props.columns 'Columns structure'
 * @param {integer} [props.rows=20] 'Number of rows to fetch'
 * @param {object} props.methods 'Object containing onUpdate, onCreate, onDelete methods.'
 * @param {function} props.methods.onUpdate 'Handles onUpdate event. Must return response object.'
 * @param {function} props.methods.onCreate 'Handles onCreate event. Must return response object.'
 * @param {function} props.methods.onDelete 'Handles onDelete event. Must return response object.'
 * @param {param} [addButtonRef] 'Refernce to the add new row button. No onClick handler is needed on the button. Just ref.'
 */
const CustomTable = ({
  api,
  query,
  queryParamName,
  sort,
  columns,
  rows = 20,
  methods,
  addButtonRef,
  modalEdit = false,
  locale,
  showHide = true,
  actionsRedirectResponsibleUser,
  onColumnEdit,
  actionsOpenModal,
  userId,
  onClickRow,
  roles,
  departments,
  onColumnRowRightClick,
  subTableColumns,
  subTableClickRow,
  subTable = false,
  ...rest
}) => {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = useForm();
  const newRowId = t('common.newEntry');
  const [validateTrigger, setValidateTrigger] = useState();
  const [subTableAvailability, setSubTableAvailability] = useState({});

  const {onUpdate, onCreate, onDelete} = methods;
  const checkSubTableAvailability = async (data) => {
    if (!subTable || !data?.length) return;
    try {
      const results = await Promise.all(
        data?.map(async (item) => {
          try {
            const response = await http.get(
              `/risks/${item?.id}/operational-events?page=0&size=1`
            );

            return {
              id: item?.id,
              hasData: Array.isArray(response?.data) && response?.data?.length > 0,
            };
          } catch (e) {
            return {
              id: item?.id,
              hasData: false,
            };
          }
        })
      );

      const availabilityMap = {};
      results.forEach((r) => {
        availabilityMap[r.id] = r.hasData;
      });

      setSubTableAvailability((prev) => ({
        ...prev,
        ...availabilityMap,
      }));
    } catch (e) {}
  };
  query = query
    ? queryParamName
      ? `${queryParamName}=${query}`
      : `name.contains=${query}`
    : '';

  sort = sort ? `sort=${sort}` : '';

  const fetchData = async () => {
    try {
      if (api.includes('?')) {
        const {data} = await http.get(`${api}`);
        setItems(data);
        setHasMore(data.length === rows);
        setPage(1);
        checkSubTableAvailability(data);
      } else {
        const {data} = await http.get(`${api}?page=0&size=${rows}&${query}`);
        setItems(data);
        setHasMore(data.length === rows);
        setPage(1);
        checkSubTableAvailability(data);
      }
    } catch (err) {}
  };

  const fetchMoreData = async () => {
    try {
      if (api.includes('?')) {
        const {data} = await http.get(`${api}&page=${page}&size=${rows}`);
        setItems([...items, ...data]);
        setHasMore(data.length === rows);
        setPage((page) => page + 1);
        checkSubTableAvailability(data);
      } else {
        const {data} = await http.get(
          `${api}?page=${page}&size=${rows}&${query}`
        );
        setItems([...items, ...data]);
        setHasMore(data.length === rows);
        setPage((page) => page + 1);
        checkSubTableAvailability(data);
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (api !== undefined && api !== '') {
      fetchData();
    }
  }, [api]);

  const isEditing = (record) => record.id === editingRecord?.id;

  const renderInput = ({inputType, ...rest}) => {
    switch (inputType) {
      case 'text':
        return <Input {...rest} />;
      case 'number':
        return <InputNumber {...rest} />;
      case 'select':
        return <Select dropdownMatchSelectWidth={false} {...rest} />;
      case 'selectMulti':
        return (
          <Select
            mode="multiple"
            allowClear
            showArrow
            showSearch={false}
            dropdownMatchSelectWidth={false}
            {...rest}
          />
        );
      case 'customSelect':
        return (
          <Select
            // mode="multiple"
            allowClear
            showArrow
            showSearch={true}
            dropdownMatchSelectWidth={false}
            {...rest}
          />
        );
      default:
        return <Input {...rest} />;
    }
  };

  const editableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    rules,
    record,
    index,
    children,
    options,
    disabled,
    min,
    max,
    placeholder,
    ...rest
  }) => {
    return (
      <td {...rest}>
        {editing ? (
          <Form.Item name={dataIndex} rules={rules}>
            {renderInput({
              inputType,
              disabled,
              options,
              // onPressEnter: handleUpdate,
              min,
              max,
              placeholder,
            })}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.inputType,
        dataIndex: col.dataIndex,
        title: col.title,
        rules: col.rules,
        editing: isEditing(record),
        options: col.options,
        disabled: col.disabled,
        min: col.min,
        max: col.max,
        placeholder: col.placeholder,
      }),
    };
  });

  const handleAdd = () => {
    if (editingRecord) {
      return Toast.fire({
        title: t('message.cancelOrSave'),
        icon: 'warning',
      });
    }
    const newRow = {};
    for (let key of columns) {
      if (key.inputType === 'selectMulti') newRow[key.dataIndex] = [];
      else newRow[key.dataIndex] = null;
    }
    newRow.id = newRowId;
    form.setFieldsValue({...newRow});
    setItems([newRow, ...items]);
    setEditingRecord(newRow);
  };

  const handleEdit = (record) => {
    if (editingRecord) {
      return Toast.fire({
        title: t('message.cancelOrSave'),
        icon: 'warning',
      });
    }
    const formatedRecord = {...record};
    for (let key in formatedRecord) {
      if (Array.isArray(formatedRecord[key])) {
        formatedRecord[key] = formatedRecord[key].map((item) => item.id);
      }
    }
    form.setFieldsValue({...formatedRecord});
    setEditingRecord(formatedRecord);
  };

  const handleCancel = (record) => {
    if (record.id === newRowId)
      setItems((items) => {
        const filtered = items.filter((item) => item.id !== newRowId);
        return filtered;
      });
    setEditingRecord(null);
  };

  const handleUpdate = async () => {
    try {
      const data = await form.validateFields();
      const response = await onUpdate(data, editingRecord, form);
      if (response.status === 200) {
        const index = items.findIndex((item) => item.id === response.data.id);
        const updatedItems = [...items];
        updatedItems[index] = {...response.data};
        setItems(updatedItems);
        setEditingRecord('');
      }
    } catch (err) {}
  };

  const handleCreate = async () => {
    try {
      const data = await form.validateFields();
      data.id = null; // Removes false id
      const response = await onCreate(data, form);
      if (response.status === 201) {
        const index = items.findIndex((item) => item.id === newRowId);
        const updatedItems = [...items];
        updatedItems[index] = {...response.data};
        setItems(updatedItems);
        setEditingRecord('');
      }
    } catch (err) {}
  };

  const handleDelete = async (record) => {
    try {
      const response = await onDelete(record);
      if (response.status === 204) {
        const filteredItems = items.filter((item) => item.id !== record.id);
        setItems(filteredItems);
      }
    } catch (err) {}
  };

  mergedColumns.push({
    key: 'actions',
    ellipsis: true,
    align: 'right',
    className: 'actions',
    // width: 70,
    render: (record) => {
      if (actionsRedirectResponsibleUser && record.riskStatusId !== 4) {
        let editDelete = false;
        let editDeleteStatus = false;
        let editDeleteVerified = false;
        departments.forEach((department) => {
          if (record.departmentSourceId === department.id) {
            editDelete = true;
          }
        });
        if (editDelete) {
          if (record.riskStatusId === 1 || record.riskStatusId === 2) {
            editDeleteStatus = true;
          }
        }
        if (record.riskStatusId === 3) {
          departments.forEach((department) => {
            if (record.departmentStakeholderId === department.id) {
              editDeleteVerified = true;
            }
          });
        }
        return (
          <>
            {/* {roles.userRiskSector || editDeleteStatus || editDeleteVerified? (
              <Tooltip title={t(actionsRedirectResponsibleUser.editTooltip)}>
                <button
                  type="button"
                  className="btn p-0 mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onColumnEdit(record.id);
                  }}
                >
                  <EditImg />
                </button>
              </Tooltip>
            ) : null} */}
            {/*   {roles.userRiskSector || editDeleteStatus || editDeleteVerified ? (
              <Tooltip title={t(actionsRedirectResponsibleUser.deleteTooltip)}>
                <button
                  type="button"
                  className="btn p-0 mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(record);
                  }}
                >
                  <DeleteImg />
                </button>
              </Tooltip>
            ) : null} */}
          </>
        );
      } else {
        if (isEditing(record))
          return (
            <div className="actions">
              <button
                className="btn p-0 mr-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel(record);
                }}
              >
                <CancelImg />
              </button>
              <button
                className="btn p-0 pb-3 pt-2"
                onClick={() =>
                  record.id === newRowId
                    ? handleCreate(record)
                    : handleUpdate(record)
                }
              >
                <TickImg />
              </button>
            </div>
          );
        else
          return (
            <>
              {/* <button
                type="button"
                className="btn p-0 mr-2"
                onClick={(e) => {
                  e.stopPropagation();
                  if (modalEdit) return onUpdate(null, record);
                  handleEdit(record);
                }}
              >
                <EditImg />
              </button> */}
              {/*  <button
                type="button"
                className="btn p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(record);
                }}
              >
                <DeleteImg />
              </button> */}
            </>
          );
      }
    },
  });

  if (addButtonRef && addButtonRef.current)
    addButtonRef.current.onclick = handleAdd;

  const loader = (
    <div className="text-center mt-3">
      <Spinner variant="primary" animation="border" />
    </div>
  );

  useEffect(() => {
    setValidateTrigger('onFinish');
  }, [editingRecord]);

  return (
    <InfiniteScroll
      next={fetchMoreData}
      hasMore={hasMore}
      loader={loader}
      scrollableTarget="app-body"
      dataLength={items.length}
    >
      <Form
        form={form}
        validateTrigger={validateTrigger}
        onFinishFailed={() => {
          setValidateTrigger('onChange');
          form.validateFields(Object.keys(editingRecord));
        }}
      >
        <Table
          components={{body: {cell: editableCell}}}
          columns={mergedColumns}
          dataSource={items}
          pagination={false}
          sticky={true}
          rowKey={(record) => record.id}
          onRow={(record) => (
            localStorage.setItem(
              'previous_link',
              window.location.pathname.toString()
            ),
            {
              onClick: (e) => {
                e.stopPropagation();
                e.ctrlKey
                  ? onColumnRowRightClick(record.id)
                  : onClickRow(record.id);
              },
              onContextMenu: (e) =>
                onColumnRowRightClick ? onColumnRowRightClick(record.id) : null,
            }
          )}
          locale={{emptyText: t('common.noData')}}
          expandable={subTable && {
            expandedRowRender: (record) => (
              <SubTableRisks
                prefixCls={'subTable'}
                pagination={false}
                columns={subTableColumns}
                onClickRow={subTableClickRow}
                api={`/risks/${record?.id}/operational-events`}
              />
            ),
            rowExpandable: (record) => !!subTableAvailability[record.id],
            expandIcon: ({expanded, onExpand, record}) =>
              subTableAvailability[record.id] ? (
                expanded ? (
                  <img
                    width={19}
                    height={19}
                    src={process.env.PUBLIC_URL + '/images/rightArrow.svg'}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                  />
                ) : (
                  <img
                    width={19}
                    height={19}
                    src={process.env.PUBLIC_URL + '/images/downArrow.svg'}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                  />
                )
              ) : null,
          }}
          {...rest}
        />
      </Form>
    </InfiniteScroll>
  );
};

export default CustomTable;
